import { Message } from '../types/Conversation';

const URL_PATTERN = /https?:\/\/[^\s]+/i;

export const messageBodyContainsUrl = (value: string): boolean => URL_PATTERN.test(value.trim());

export const findLatestInboundWhatsAppMessageId = (messages: Message[]): string | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.source !== 'whatsapp') {
      continue;
    }

    if (message.sender.type === 'customer' || message.sender.type === 'lead') {
      return message.id;
    }
  }

  return undefined;
};

export const findLatestInboundInstagramMessageId = (messages: Message[]): string | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.source !== 'instagram') {
      continue;
    }

    if (message.sender.type === 'customer' || message.sender.type === 'lead') {
      return message.id;
    }
  }

  return undefined;
};

export const findLatestInboundMessengerMessageId = (messages: Message[]): string | undefined => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.source !== 'messenger') {
      continue;
    }

    if (message.sender.type === 'customer' || message.sender.type === 'lead') {
      return message.id;
    }
  }

  return undefined;
};

export const conversationHasInstagramMessages = (messages: Message[]): boolean =>
  messages.some(message => message.source === 'instagram');

export const conversationHasMessengerMessages = (messages: Message[]): boolean =>
  messages.some(message => message.source === 'messenger');

export const conversationHasWhatsAppMessages = (messages: Message[]): boolean =>
  messages.some(message => message.source === 'whatsapp');

export const buildWhatsAppDeliveryTooltip = (
  message: Message,
  statusLabel: string,
  labels: {
    whatsAppStatus: string;
    whatsAppMessageId: string;
    whatsAppCallbackRef: string;
  },
): string => {
  const lines = [statusLabel];

  if (message.whatsAppStatusValue) {
    lines.push(`${labels.whatsAppStatus}: ${message.whatsAppStatusValue}`);
  }

  if (message.whatsAppMessageId) {
    lines.push(`${labels.whatsAppMessageId}: ${message.whatsAppMessageId}`);
  }

  if (message.whatsAppBizOpaqueCallbackData) {
    lines.push(`${labels.whatsAppCallbackRef}: ${message.whatsAppBizOpaqueCallbackData}`);
  }

  return lines.join('\n');
};

export const buildInstagramDeliveryTooltip = (
  message: Message,
  statusLabel: string,
  labels: {
    instagramStatus: string;
    instagramMessageId: string;
    instagramRecipientId: string;
    instagramPrivateReplyCommentId: string;
  },
): string => {
  const lines = [statusLabel];

  if (message.instagramStatusValue) {
    lines.push(`${labels.instagramStatus}: ${message.instagramStatusValue}`);
  }

  if (message.instagramMessageId) {
    lines.push(`${labels.instagramMessageId}: ${message.instagramMessageId}`);
  }

  if (message.instagramRecipientId) {
    lines.push(`${labels.instagramRecipientId}: ${message.instagramRecipientId}`);
  }

  if (message.instagramPrivateReplyCommentId) {
    lines.push(`${labels.instagramPrivateReplyCommentId}: ${message.instagramPrivateReplyCommentId}`);
  }

  return lines.join('\n');
};

export const buildMessengerDeliveryTooltip = (
  message: Message,
  statusLabel: string,
  labels: {
    messengerStatus: string;
    messengerMessageId: string;
    messengerRecipientId: string;
  },
): string => {
  const lines = [statusLabel];

  if (message.messengerStatusValue) {
    lines.push(`${labels.messengerStatus}: ${message.messengerStatusValue}`);
  }

  if (message.messengerMessageId) {
    lines.push(`${labels.messengerMessageId}: ${message.messengerMessageId}`);
  }

  if (message.messengerRecipientId) {
    lines.push(`${labels.messengerRecipientId}: ${message.messengerRecipientId}`);
  }

  return lines.join('\n');
};

export const buildChannelDeliveryTooltip = (
  message: Message,
  statusLabel: string,
  labels: {
    whatsAppStatus: string;
    whatsAppMessageId: string;
    whatsAppCallbackRef: string;
    instagramStatus: string;
    instagramMessageId: string;
    instagramRecipientId: string;
    instagramPrivateReplyCommentId: string;
    messengerStatus: string;
    messengerMessageId: string;
    messengerRecipientId: string;
  },
): string => {
  if (message.instagramMessageId || message.instagramStatusValue || message.instagramRecipientId) {
    return buildInstagramDeliveryTooltip(message, statusLabel, labels);
  }

  if (message.messengerMessageId || message.messengerStatusValue || message.messengerRecipientId) {
    return buildMessengerDeliveryTooltip(message, statusLabel, labels);
  }

  if (message.whatsAppStatusValue || message.whatsAppMessageId || message.whatsAppBizOpaqueCallbackData) {
    return buildWhatsAppDeliveryTooltip(message, statusLabel, labels);
  }

  return statusLabel;
};
